import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import QRCode from 'qrcode'
import { authApi } from '../../api/auth'
import { ApiError } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import type { TotpSetupResponse } from '../../api/types'
import {
  backupCodesFileName,
  classifyOtpInput,
  formatBackupCodes,
  groupSecret,
  isOtpSubmittable,
  isTotpCode,
} from '../../utils/twoFactor'

/** Порог «кодов почти не осталось». */
const LOW_BACKUP_CODES = 3

/**
 * Секция 2FA для ProfilePage и экрана принудительной настройки (6.1).
 *
 * Состояния:
 *  - disabled + setupData=null: кнопка «Включить»
 *  - disabled + setupData≠null: QR + ручной ключ + резервные коды + поле кода
 *  - enabled: статус, остаток резервных кодов, перевыпуск, «Отключить»
 *    (если политика не делает 2FA обязательной)
 */
export function TotpSection() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { teacher } = useAuth()
  const status = useQuery({
    queryKey: ['auth', 'totp', 'status'],
    queryFn: () => authApi.totpStatus(),
  })

  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [disableCode, setDisableCode] = useState('')
  const [confirmingDisable, setConfirmingDisable] = useState(false)
  const [regenCode, setRegenCode] = useState('')
  const [regenerating, setRegenerating] = useState(false)
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Когда получили setupData — генерируем PNG для QR
  useEffect(() => {
    if (setupData) {
      QRCode.toDataURL(setupData.otpauthUrl, { width: 220, margin: 1 })
        .then(setQrDataUrl)
        .catch((e) => setError(String(e)))
    } else {
      setQrDataUrl(null)
    }
  }, [setupData])

  const refreshStatus = () => qc.invalidateQueries({ queryKey: ['auth', 'totp', 'status'] })

  const setupMut = useMutation({
    mutationFn: () => authApi.totpSetup(),
    onSuccess: (data) => {
      setSetupData(data)
      setError(null)
      setCode('')
    },
    onError: (e) => setError(extractMessage(e, t)),
  })

  const verifyMut = useMutation({
    mutationFn: () => authApi.totpVerify(classifyOtpInput(code).value),
    onSuccess: async () => {
      // При принудительной настройке сервер уже выдал полноценную сессию (cookie);
      // обновлённый статус снимет TotpEnforcementGate.
      setSetupData(null)
      setCode('')
      setError(null)
      await refreshStatus()
    },
    onError: (e) => setError(extractMessage(e, t)),
  })

  const disableMut = useMutation({
    mutationFn: () => authApi.totpDisable(classifyOtpInput(disableCode).value),
    onSuccess: async () => {
      setConfirmingDisable(false)
      setDisableCode('')
      setError(null)
      await refreshStatus()
      await qc.invalidateQueries({ queryKey: ['auth', 'trusted-devices'] })
    },
    onError: (e) => setError(extractMessage(e, t)),
  })

  const regenMut = useMutation({
    mutationFn: () => authApi.regenerateBackupCodes(classifyOtpInput(regenCode).value),
    onSuccess: async (codes) => {
      setFreshCodes(codes)
      setRegenerating(false)
      setRegenCode('')
      setError(null)
      await refreshStatus()
    },
    onError: (e) => setError(extractMessage(e, t)),
  })

  if (status.isLoading) return <div className="text-sm text-gray-500">{t('common.loading')}</div>

  const enabled = status.data?.enabled === true
  const totpRequired = status.data?.required === true
  const remaining = status.data?.backupCodesRemaining ?? 0
  const account = teacher?.email ?? ''

  const errorBox = error && (
    <div role="alert" className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
      {error}
    </div>
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span
          className={`inline-block w-2 h-2 rounded-full ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}
        />
        <span className="text-sm">{enabled ? t('totpSection.statusEnabled') : t('totpSection.statusDisabled')}</span>
      </div>

      <p className="text-xs text-gray-500">
        {t('totpSection.description')}
      </p>
      <ul className="text-xs text-gray-600 list-disc pl-5 space-y-1">
        <li>
          <a
            href="https://id.yandex.ru/promo/key"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-medium"
          >
            {t('totpSection.appYandex')}
          </a>{' '}
          {t('totpSection.appYandexNote')}
        </li>
        <li>{t('totpSection.appAegis')}</li>
        <li>{t('totpSection.app2fas')}</li>
        <li>{t('totpSection.appGoogleMs')}</li>
        <li>{t('totpSection.appManagers')}</li>
      </ul>
      <p className="text-xs text-gray-500">
        {t('totpSection.mandatoryNote')}
      </p>

      {!enabled && !setupData && (
        <>
          {errorBox}
          <button
            type="button"
            onClick={() => setupMut.mutate()}
            disabled={setupMut.isPending}
            className="btn-primary text-sm"
          >
            {setupMut.isPending ? t('totpSection.preparing') : t('totpSection.enable')}
          </button>
        </>
      )}

      {!enabled && setupData && (
        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div>
            <div className="text-sm font-medium mb-2">{t('totpSection.step1')}</div>
            {qrDataUrl && (
              <img
                src={qrDataUrl}
                alt={t('totpSection.qrAlt')}
                className="border border-gray-300 rounded mx-auto"
              />
            )}
            <details className="mt-2 text-xs text-gray-500">
              <summary className="cursor-pointer hover:text-primary">
                {t('totpSection.manualEntry')}
              </summary>
              <code className="block mt-1 text-[12px] tracking-wider break-all bg-white p-2 rounded border border-gray-200 select-all">
                {groupSecret(setupData.secret)}
              </code>
            </details>
          </div>

          <div>
            <div className="text-sm font-medium mb-2">{t('totpSection.step2')}</div>
            <p className="text-xs text-gray-500 mb-2">
              {t('totpSection.backupNote')}
            </p>
            <BackupCodesList codes={setupData.backupCodes} account={account} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" htmlFor="totp-setup-code">
              {t('totpSection.step3')}
            </label>
            <input
              id="totp-setup-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className="input-field text-center tracking-widest"
              maxLength={7}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </div>

          {errorBox}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => verifyMut.mutate()}
              disabled={!isTotpCode(code) || verifyMut.isPending}
              className="btn-primary text-sm flex-1"
            >
              {verifyMut.isPending ? t('totpSection.verifying') : t('totpSection.activate')}
            </button>
            <button
              type="button"
              onClick={() => { setSetupData(null); setCode(''); setError(null) }}
              className="text-sm text-gray-500 hover:text-red-600 px-3"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {enabled && (
        <div className={`text-sm ${remaining <= LOW_BACKUP_CODES ? 'text-amber-700' : 'text-gray-600'}`}>
          {t('totpSection.backupRemaining', { n: remaining })}
          {remaining <= LOW_BACKUP_CODES && <> {t('totpSection.backupLow')}</>}
        </div>
      )}

      {enabled && freshCodes && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
          <div className="text-sm font-medium">{t('totpSection.newCodesTitle')}</div>
          <p className="text-xs text-gray-600">{t('totpSection.newCodesNote')}</p>
          <BackupCodesList codes={freshCodes} account={account} />
          <button type="button" onClick={() => setFreshCodes(null)} className="text-sm btn-outline py-1">
            {t('totpSection.codesSaved')}
          </button>
        </div>
      )}

      {enabled && !freshCodes && !confirmingDisable && !regenerating && (
        <button
          type="button"
          onClick={() => { setRegenerating(true); setError(null) }}
          className="text-sm text-primary hover:underline block"
        >
          {t('totpSection.regenerate')}
        </button>
      )}

      {enabled && regenerating && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
          <p className="text-sm text-gray-700">{t('totpSection.regenerateNote')}</p>
          <input
            type="text"
            value={regenCode}
            onChange={(e) => setRegenCode(e.target.value)}
            placeholder="123456"
            className="input-field text-center tracking-widest"
            maxLength={13}
            autoComplete="one-time-code"
            aria-label={t('totpSection.regenerate')}
          />
          {errorBox}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => regenMut.mutate()}
              disabled={!isOtpSubmittable(regenCode) || regenMut.isPending}
              className="btn-primary text-sm"
            >
              {regenMut.isPending ? t('totpSection.regenerating') : t('totpSection.regenerate')}
            </button>
            <button
              type="button"
              onClick={() => { setRegenerating(false); setRegenCode(''); setError(null) }}
              className="text-sm text-gray-500 hover:text-gray-700 px-3"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {enabled && !confirmingDisable && !regenerating && (
        totpRequired ? (
          <div className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded p-3">
            🔒 {t('totpSection.requiredCannotDisable')}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setConfirmingDisable(true); setError(null) }}
            className="text-sm text-red-600 hover:underline"
          >
            {t('totpSection.disable')}
          </button>
        )
      )}

      {enabled && confirmingDisable && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
          <p className="text-sm text-red-700">
            {t('totpSection.disableConfirmNote')}
          </p>
          <input
            type="text"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            placeholder="123456"
            className="input-field text-center tracking-widest"
            maxLength={13}
            autoComplete="one-time-code"
            aria-label={t('totpSection.disable')}
          />
          {errorBox}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => disableMut.mutate()}
              disabled={!isOtpSubmittable(disableCode) || disableMut.isPending}
              className="text-sm bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 disabled:opacity-50"
            >
              {disableMut.isPending ? t('totpSection.disabling') : t('totpSection.disable')}
            </button>
            <button
              type="button"
              onClick={() => { setConfirmingDisable(false); setDisableCode(''); setError(null) }}
              className="text-sm text-gray-500 hover:text-gray-700 px-3"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** Резервные коды + «скопировать» / «скачать .txt». */
function BackupCodesList({ codes, account }: { codes: string[]; account: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const text = () => formatBackupCodes(codes, account)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text())
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // буфер обмена недоступен (нет HTTPS/прав) — остаётся скачивание
    }
  }

  const download = () => {
    const url = URL.createObjectURL(new Blob([text()], { type: 'text/plain;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = backupCodesFileName()
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 bg-white p-2 rounded border border-gray-200 font-mono text-xs">
        {codes.map((c) => (
          <code key={c}>{c}</code>
        ))}
      </div>
      <div className="flex gap-4 mt-2">
        <button type="button" onClick={() => { void copy() }} className="text-xs text-primary hover:underline">
          {copied ? t('common.copied') : t('totpSection.copyAll')}
        </button>
        <button type="button" onClick={download} className="text-xs text-primary hover:underline">
          {t('totpSection.downloadCodes')}
        </button>
      </div>
    </>
  )
}

function extractMessage(e: unknown, t: TFunction): string {
  if (e instanceof ApiError) {
    if (e.status === 400) return t('totpSection.errInvalidCode')
    return e.message
  }
  if (e instanceof Error) return e.message
  return t('totpSection.errUnknown')
}
